import * as THREE from 'three';
import { OrbFactory } from '../../orbs/OrbFactory';
import { liveStreamRegistry } from './LiveStreamRegistry';
import { registerOrbStreamAttachment } from './OrbStreamAttachments';
import { getConnectionTypeDefinition } from './ConnectionTypeDefinitions';
const gatewayConnectionDefinition = getConnectionTypeDefinition('gateway');
const TRAFFIC_STREAM_URL = gatewayConnectionDefinition.streamUrl;
const TRAFFIC_STREAM_DEBUG = true;
const RECONNECT_DELAY_MS = 5000;
const ENABLE_TRAFFIC_WISPS = true;
const ENABLE_TRAFFIC_FLIGHTS = true;
const ENABLE_REQUEST_TIME_DRIVEN_FLIGHT_DURATION = true;
const TRAFFIC_WISP_GROUP_NAME = 'systemTrafficStreamBursts';
const MAX_ACTIVE_WISPS = 140;
const WISP_SIZE_MULTIPLIER = 100;
const WISP_SPAWN_REGION_SCALE = 10;
const MAX_ACTIVE_FLIGHTS = 120;
const MIN_FLIGHT_DURATION_MS = 180;
const MAX_FLIGHT_DURATION_MS = 8000;
const DEFAULT_FLIGHT_DURATION_MS = 650;
const COMPRESSED_FLIGHT_DURATION_SCALE = 550;
const FADE_START_T = 0.78;
const sphereGeometry = new THREE.SphereGeometry(1, 24, 24);
class TrafficStreamBridge {
    constructor() {
        this.activeBursts = [];
        this.activeFlights = [];
        this.connectionBootstrapScheduled = false;
    }
    attachScene(scene, endpointProvider, endpointIsActive) {
        this.scene = scene;
        if (endpointProvider) {
            this.endpointProvider = endpointProvider;
            this.endpointIsActive = endpointIsActive !== null && endpointIsActive !== void 0 ? endpointIsActive : (() => true);
        }
        if (!this.wispGroup) {
            const existingGroup = scene.getObjectByName(TRAFFIC_WISP_GROUP_NAME);
            if (existingGroup instanceof THREE.Group) {
                this.wispGroup = existingGroup;
            }
            else {
                this.wispGroup = new THREE.Group();
                this.wispGroup.name = TRAFFIC_WISP_GROUP_NAME;
                scene.add(this.wispGroup);
            }
        }
        this.scheduleInitialConnection();
        this.startAnimationLoop();
    }
    scheduleInitialConnection() {
        if (this.connectionBootstrapScheduled) {
            return;
        }
        this.connectionBootstrapScheduled = true;
        const connectAfterPaint = () => {
            this.connectionBootstrapScheduled = false;
            this.ensureConnected();
        };
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(() => {
                window.requestAnimationFrame(connectAfterPaint);
            });
            return;
        }
        setTimeout(connectAfterPaint, 0);
    }
    ensureConnected() {
        if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
            return;
        }
        this.socket = new WebSocket(TRAFFIC_STREAM_URL);
        this.socket.onopen = () => {
            liveStreamRegistry.setConnectionState('gateway', true);
            this.debug('connected', { url: TRAFFIC_STREAM_URL });
        };
        this.socket.onmessage = (event) => {
            this.handleMessage(event.data);
        };
        this.socket.onerror = (event) => {
            this.debug('websocket error', event);
        };
        this.socket.onclose = () => {
            liveStreamRegistry.setConnectionState('gateway', false);
            this.debug('disconnected; retrying', { retryInMs: RECONNECT_DELAY_MS });
            window.setTimeout(() => this.ensureConnected(), RECONNECT_DELAY_MS);
        };
    }
    handleMessage(raw) {
        if (typeof raw !== 'string') {
            return;
        }
        let parsed;
        try {
            parsed = JSON.parse(raw);
        }
        catch (_a) {
            this.debug('invalid JSON payload ignored');
            return;
        }
        if (!this.isTrafficPayload(parsed)) {
            this.debug('payload shape rejected');
            return;
        }
        if (!this.endpointsAreActive()) {
            this.cleanupAttachedVisuals();
            return;
        }
        if (ENABLE_TRAFFIC_WISPS) {
            this.spawnBurst(parsed);
        }
        if (ENABLE_TRAFFIC_FLIGHTS) {
            this.spawnFlight(parsed);
        }
    }
    spawnBurst(payload) {
        var _a, _b, _c, _d;
        if (!this.wispGroup) {
            return;
        }
        const status = (_b = (_a = payload.response) === null || _a === void 0 ? void 0 : _a.status) !== null && _b !== void 0 ? _b : 0;
        const bodyBytesSent = (_d = (_c = payload.response) === null || _c === void 0 ? void 0 : _c.bodyBytesSent) !== null && _d !== void 0 ? _d : 0;
        const baseSize = Math.min(14, Math.max(2, (bodyBytesSent / 8000) * 14));
        const sizeFromBody = baseSize * WISP_SIZE_MULTIPLIER;
        const baseRadius = 380 * WISP_SPAWN_REGION_SCALE;
        const angle = Math.random() * Math.PI * 2;
        const radiusJitter = (Math.random() - 0.5) * 160 * WISP_SPAWN_REGION_SCALE;
        const y = 80 * WISP_SPAWN_REGION_SCALE + Math.random() * 260 * WISP_SPAWN_REGION_SCALE;
        const material = new THREE.MeshStandardMaterial({
            color: this.colorFromStatus(status),
            emissive: this.colorFromStatus(status),
            emissiveIntensity: 0.45,
            roughness: 0.35,
            metalness: 0.15,
            transparent: true,
            opacity: 0.95,
        });
        const mesh = new THREE.Mesh(sphereGeometry, material);
        mesh.position.set(200 + Math.cos(angle) * (baseRadius + radiusJitter), y, -280 + Math.sin(angle) * (baseRadius + radiusJitter));
        mesh.scale.setScalar(sizeFromBody);
        this.wispGroup.add(mesh);
        this.activeBursts.push({
            mesh,
            bornAtMs: performance.now(),
            lifetimeMs: 2600,
            spin: (Math.random() - 0.5) * 0.07,
        });
        this.enforceBurstCap();
    }
    spawnFlight(payload) {
        var _a;
        if (!this.scene || !this.endpointProvider) {
            return;
        }
        const path = this.endpointProvider();
        if (!path) {
            return;
        }
        const orb = OrbFactory.createOrbByType(gatewayConnectionDefinition.visuals.flightOrbType, (_a = payload.request.method) !== null && _a !== void 0 ? _a : 'HTTP');
        if (!orb) {
            return;
        }
        const start = path.start.clone();
        const end = path.end.clone();
        orb.setPosition(start);
        orb.addToScene(this.scene);
        const lineGeometry = new THREE.BufferGeometry().setFromPoints([start, end]);
        const lineMaterial = new THREE.LineBasicMaterial({
            color: gatewayConnectionDefinition.visuals.lineColor,
            linewidth: gatewayConnectionDefinition.visuals.lineWidth,
            transparent: true,
            opacity: gatewayConnectionDefinition.visuals.lineOpacity,
            depthWrite: false,
        });
        const line = new THREE.Line(lineGeometry, lineMaterial);
        this.scene.add(line);
        const durationMs = this.computeDurationMs(payload.response.requestTimeSeconds);
        this.activeFlights.push({
            orb,
            line,
            start,
            end,
            startedAtMs: performance.now(),
            durationMs,
        });
        this.enforceFlightCap();
        this.debug('spawned flight', {
            method: payload.request.method,
            durationMs,
            activeFlights: this.activeFlights.length,
        });
    }
    startAnimationLoop() {
        if (this.animationFrameId !== undefined) {
            return;
        }
        const animate = () => {
            const now = performance.now();
            if (!this.endpointsAreActive()) {
                this.cleanupAttachedVisuals();
            }
            this.activeBursts = this.activeBursts.filter((burst) => {
                const elapsed = now - burst.bornAtMs;
                const t = elapsed / burst.lifetimeMs;
                if (t >= 1) {
                    this.disposeBurst(burst);
                    return false;
                }
                burst.mesh.position.y += 0.28;
                burst.mesh.rotation.y += burst.spin;
                const opacity = Math.max(0.03, 1 - t);
                const material = burst.mesh.material;
                material.opacity = opacity;
                burst.mesh.scale.multiplyScalar(1.004);
                return true;
            });
            this.activeFlights = this.activeFlights.filter((flight) => {
                const elapsed = now - flight.startedAtMs;
                const t = elapsed / flight.durationMs;
                if (t >= 1) {
                    this.disposeFlight(flight);
                    return false;
                }
                const smoothT = this.smoothStep(Math.min(Math.max(t, 0), 1));
                const nextPosition = new THREE.Vector3().lerpVectors(flight.start, flight.end, smoothT);
                flight.orb.setPosition(nextPosition);
                const fade = t >= FADE_START_T ? 1 - (t - FADE_START_T) / (1 - FADE_START_T) : 1;
                this.setOrbOpacity(flight.orb, Math.max(0, fade));
                const lineMaterial = flight.line.material;
                lineMaterial.opacity = Math.max(0, gatewayConnectionDefinition.visuals.lineOpacity * fade);
                return true;
            });
            this.animationFrameId = requestAnimationFrame(animate);
        };
        this.animationFrameId = requestAnimationFrame(animate);
    }
    endpointsAreActive() {
        if (!this.endpointIsActive) {
            return true;
        }
        return this.endpointIsActive();
    }
    cleanupAttachedVisuals() {
        this.activeBursts.forEach((burst) => this.disposeBurst(burst));
        this.activeBursts = [];
        this.activeFlights.forEach((flight) => this.disposeFlight(flight));
        this.activeFlights = [];
    }
    setOrbOpacity(orb, opacity) {
        if (!orb.group) {
            return;
        }
        orb.group.traverse((obj) => {
            if (!(obj instanceof THREE.Mesh || obj instanceof THREE.Sprite)) {
                return;
            }
            const mat = obj.material;
            if (!mat) {
                return;
            }
            const materials = Array.isArray(mat) ? mat : [mat];
            for (const material of materials) {
                if ('transparent' in material) {
                    material.transparent = true;
                }
                if ('opacity' in material) {
                    material.opacity = opacity;
                }
                material.needsUpdate = true;
            }
        });
    }
    disposeBurst(burst) {
        if (burst.mesh.parent) {
            burst.mesh.parent.remove(burst.mesh);
        }
        burst.mesh.material.dispose();
    }
    disposeFlight(flight) {
        if (this.scene && flight.orb.group) {
            try {
                flight.orb.removeFromScene(this.scene, true);
            }
            catch (_a) {
                this.scene.remove(flight.orb.group);
            }
        }
        if (flight.line.parent) {
            flight.line.parent.remove(flight.line);
        }
        flight.line.geometry.dispose();
        flight.line.material.dispose();
    }
    enforceBurstCap() {
        while (this.activeBursts.length > MAX_ACTIVE_WISPS) {
            const oldest = this.activeBursts.shift();
            if (!oldest) {
                return;
            }
            this.disposeBurst(oldest);
        }
    }
    enforceFlightCap() {
        while (this.activeFlights.length > MAX_ACTIVE_FLIGHTS) {
            const oldest = this.activeFlights.shift();
            if (!oldest) {
                return;
            }
            this.disposeFlight(oldest);
        }
    }
    computeDurationMs(requestTimeSeconds) {
        if (typeof requestTimeSeconds !== 'number' || !Number.isFinite(requestTimeSeconds)) {
            return DEFAULT_FLIGHT_DURATION_MS;
        }
        const ms = ENABLE_REQUEST_TIME_DRIVEN_FLIGHT_DURATION
            ? requestTimeSeconds * 1000
            : requestTimeSeconds * COMPRESSED_FLIGHT_DURATION_SCALE;
        return Math.min(MAX_FLIGHT_DURATION_MS, Math.max(MIN_FLIGHT_DURATION_MS, ms));
    }
    smoothStep(value) {
        return value * value * (3 - 2 * value);
    }
    colorFromStatus(status) {
        if (status >= 500)
            return 0xff4b4b;
        if (status >= 400)
            return 0xff9f43;
        if (status >= 300)
            return 0xf6e05e;
        return 0x4ade80;
    }
    isTrafficPayload(payload) {
        if (!payload || typeof payload !== 'object') {
            return false;
        }
        const maybe = payload;
        if (typeof maybe.occurredAt !== 'string') {
            return false;
        }
        if (!maybe.request || typeof maybe.request !== 'object') {
            return false;
        }
        if (!maybe.response || typeof maybe.response !== 'object') {
            return false;
        }
        return true;
    }
    debug(message, data) {
        if (!TRAFFIC_STREAM_DEBUG) {
            return;
        }
        console.log(`[TrafficStreamBridge] ${message}`, data !== null && data !== void 0 ? data : '');
    }
}
function resolveSingletonBridge() {
    const globalWindow = globalThis;
    if (!globalWindow.__trafficStreamBridge) {
        globalWindow.__trafficStreamBridge = new TrafficStreamBridge();
    }
    return globalWindow.__trafficStreamBridge;
}
function pathProviderFromOrbs(pointAOrb, pointBOrb) {
    const isActive = () => { var _a, _b; return Boolean(((_a = pointAOrb.group) === null || _a === void 0 ? void 0 : _a.parent) && ((_b = pointBOrb.group) === null || _b === void 0 ? void 0 : _b.parent)); };
    const provider = () => {
        var _a, _b;
        if (!isActive()) {
            return null;
        }
        const start = (_a = pointAOrb.group) === null || _a === void 0 ? void 0 : _a.getWorldPosition(new THREE.Vector3()).clone();
        const end = (_b = pointBOrb.group) === null || _b === void 0 ? void 0 : _b.getWorldPosition(new THREE.Vector3()).clone();
        if (!start || !end) {
            return null;
        }
        return { start, end };
    };
    return { provider, isActive };
}
function pathProviderFromPath(path) {
    if ('getStart' in path && 'getEnd' in path) {
        return () => ({
            start: path.getStart().clone(),
            end: path.getEnd().clone(),
        });
    }
    return () => ({
        start: path.start.clone(),
        end: path.end.clone(),
    });
}
export function attachTrafficStreamToSystemWorld(scene, path) {
    const bridge = resolveSingletonBridge();
    bridge.attachScene(scene, pathProviderFromPath(path));
}
export function attachTrafficStreamBetweenOrbs(scene, pointAOrb, pointBOrb) {
    const bridge = resolveSingletonBridge();
    const orbPath = pathProviderFromOrbs(pointAOrb, pointBOrb);
    bridge.attachScene(scene, orbPath.provider, orbPath.isActive);
}
const STREAM_ATTACHMENT_KEY = 'gateway';
function ensureTrafficAttachmentRegistered() {
    const globalScope = globalThis;
    if (globalScope.__trafficStreamAttachmentRegistered) {
        return;
    }
    registerOrbStreamAttachment(STREAM_ATTACHMENT_KEY, {
        attachBetweenOrbs: (scene, sourceOrb, targetOrb) => {
            attachTrafficStreamBetweenOrbs(scene, sourceOrb, targetOrb);
        },
    });
    globalScope.__trafficStreamAttachmentRegistered = true;
}
ensureTrafficAttachmentRegistered();
